import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AudienceWithUrlDto,
  CreateFlyerReviewAudienceDto,
} from "@swagger/typescript-fetch-goflyer";
import { messages } from "../../../../../locales/messages";
import { InviteFormGrid } from "../../styled";
import { reviewTokens } from "../../theme";
import { copyToClipboard } from "../copyToClipboard";

interface Props {
  onInvite: (
    body: CreateFlyerReviewAudienceDto,
  ) => Promise<AudienceWithUrlDto | undefined>;
}

const EMPTY: CreateFlyerReviewAudienceDto = {
  audienceKey: "",
  displayName: "",
  email: "",
  permission: CreateFlyerReviewAudienceDto.PermissionEnum.VIEW,
  expiresAt: "",
};

/**
 * Convert a display name into a stable audience key:
 *  - lowercase
 *  - replace any run of non-alphanumeric characters with a single hyphen
 *  - trim leading/trailing hyphens
 *  - cap length at 48 chars (matches typical key constraints)
 */
const slugifyAudienceKey = (input: string): string => {
  return input
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
};

const HelpAdornment = ({ tip }: { tip: string }) => (
  <InputAdornment position="end" sx={{ mr: -0.5 }}>
    <Tooltip title={tip} arrow placement="top">
      <HelpOutlineIcon
        sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }}
      />
    </Tooltip>
  </InputAdornment>
);

export function AddAudienceForm({ onInvite }: Props) {
  const { t } = useTranslation();
  const [form, setForm] = useState<CreateFlyerReviewAudienceDto>(EMPTY);
  // Track whether the user has manually edited the audience key.
  // While it's still in "auto" mode, every display-name change syncs the key.
  const [autoKey, setAutoKey] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [lastInvite, setLastInvite] = useState<AudienceWithUrlDto>();

  const canSubmit =
    !submitting &&
    form.audienceKey.trim().length > 0 &&
    form.displayName.trim().length > 0;

  const handleDisplayNameChange = (value: string) => {
    setForm((prev) => ({
      ...prev,
      displayName: value,
      audienceKey: autoKey ? slugifyAudienceKey(value) : prev.audienceKey,
    }));
  };

  const handleAudienceKeyChange = (value: string) => {
    setAutoKey(false);
    setForm((prev) => ({ ...prev, audienceKey: value }));
  };

  const regenerateKey = () => {
    setAutoKey(true);
    setForm((prev) => ({
      ...prev,
      audienceKey: slugifyAudienceKey(prev.displayName),
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const payload: CreateFlyerReviewAudienceDto = {
      ...form,
      email: form.email || undefined,
      expiresAt: form.expiresAt || undefined,
    };
    const result = await onInvite(payload);
    setSubmitting(false);
    if (result) {
      setLastInvite(result);
      setForm(EMPTY);
      setAutoKey(true);
    }
  };

  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          color: "text.secondary",
          mb: 1.5,
          fontSize: 11,
        }}
      >
        Fields marked{" "}
        <Box
          component="span"
          sx={{ color: "error.main", fontWeight: 700 }}
        >
          *
        </Box>{" "}
        are required.
      </Typography>
      <InviteFormGrid>
        <Tooltip
          title="Full name of the reviewer. The audience key is generated from this automatically."
          placement="top"
          arrow
        >
          <TextField
            size="small"
            required
            label={t(messages.Display_Name())}
            placeholder="e.g. Jane Doe"
            value={form.displayName}
            onChange={(e) => handleDisplayNameChange(e.target.value)}
            data-testid="audience-display-name-input"
            InputProps={{
              endAdornment: (
                <HelpAdornment tip="Full name shown next to comments and on the invite." />
              ),
            }}
          />
        </Tooltip>
        <Tooltip
          title="Unique identifier used in the invite URL. Auto-generated from the display name. Click the wand to regenerate."
          placement="top"
          arrow
        >
          <TextField
            size="small"
            required
            label={t(messages.Audience_Key())}
            placeholder="jane-doe"
            value={form.audienceKey}
            onChange={(e) => handleAudienceKeyChange(e.target.value)}
            data-testid="audience-key-input"
            helperText={
              autoKey && form.audienceKey
                ? "Auto-generated from display name"
                : !autoKey
                  ? "Custom key (will not auto-update)"
                  : " "
            }
            FormHelperTextProps={{
              sx: {
                ml: 0.5,
                fontSize: 10,
                color: autoKey ? "text.disabled" : "text.secondary",
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ mr: -0.5 }}>
                  <Tooltip
                    title="Regenerate from display name"
                    placement="top"
                    arrow
                  >
                    <span>
                      <IconButton
                        size="small"
                        onClick={regenerateKey}
                        disabled={!form.displayName.trim()}
                        sx={{ p: 0.25, color: "text.secondary" }}
                      >
                        <AutoFixHighOutlinedIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ),
              sx: {
                fontFamily: reviewTokens.fontMono,
                fontSize: 13,
              },
            }}
          />
        </Tooltip>
        <Tooltip
          title="Optional. If provided, an invitation email may be sent to this address."
          placement="top"
          arrow
        >
          <TextField
            size="small"
            label={`${t(messages.Email())} (optional)`}
            type="email"
            placeholder="jane@example.com"
            value={form.email || ""}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, email: e.target.value }))
            }
            data-testid="audience-email-input"
            InputProps={{
              endAdornment: (
                <HelpAdornment tip="Optional. Used only for notifications." />
              ),
            }}
          />
        </Tooltip>
        <Tooltip
          title="VIEW lets the reviewer see the flyer and add comments. EDIT also lets them propose changes."
          placement="top"
          arrow
        >
          <TextField
            size="small"
            select
            required
            label={t(messages.Permission())}
            value={form.permission}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                permission: e.target
                  .value as unknown as CreateFlyerReviewAudienceDto.PermissionEnum,
              }))
            }
            data-testid="audience-permission-input"
          >
            <MenuItem
              value={
                CreateFlyerReviewAudienceDto.PermissionEnum.VIEW as unknown as string
              }
            >
              VIEW
            </MenuItem>
            <MenuItem
              value={
                CreateFlyerReviewAudienceDto.PermissionEnum.EDIT as unknown as string
              }
            >
              EDIT
            </MenuItem>
          </TextField>
        </Tooltip>
        <Tooltip
          title="Optional. After this time the reviewer's link stops working. Leave empty for no expiry."
          placement="top"
          arrow
        >
          <TextField
            size="small"
            type="datetime-local"
            label={`${t(messages.Expires_At())} (optional)`}
            InputLabelProps={{ shrink: true }}
            value={form.expiresAt || ""}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, expiresAt: e.target.value }))
            }
            data-testid="audience-expires-input"
          />
        </Tooltip>
      </InviteFormGrid>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mt: 0.5 }}
      >
        <Typography
          variant="caption"
          sx={{
            color: canSubmit ? "success.main" : "text.disabled",
            fontWeight: 500,
          }}
        >
          {canSubmit
            ? "Ready to invite"
            : "Enter a display name to continue"}
        </Typography>
        <Tooltip
          title={
            canSubmit
              ? "Send invitation"
              : "Display name and audience key are required."
          }
          placement="top"
          arrow
        >
          <span>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<PersonAddOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={handleSubmit}
              disabled={!canSubmit}
              data-testid="audience-invite-button"
            >
              {t(messages.Invite_Reviewer())}
            </Button>
          </span>
        </Tooltip>
      </Stack>
      {lastInvite?.url ? (
        <Box
          data-testid="audience-invite-url-row"
          sx={{
            mt: 1.5,
            p: 1.25,
            borderRadius: `${reviewTokens.radiusMd}px`,
            bgcolor: reviewTokens.brandSoft,
            border: `1px solid ${reviewTokens.brand}`,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <CheckCircleOutlineIcon
            sx={{ fontSize: 16, color: reviewTokens.brand, flexShrink: 0 }}
          />
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              minWidth: 0,
              fontFamily: reviewTokens.fontMono,
              fontSize: 12,
              color: "text.primary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={lastInvite.url}
          >
            {lastInvite.url}
          </Typography>
          <Tooltip title={t(messages.Copy_Link())} arrow>
            <IconButton
              size="small"
              onClick={() =>
                copyToClipboard(lastInvite.url, t(messages.Copied()))
              }
              data-testid="audience-invite-copy-button"
              sx={{ color: reviewTokens.brand }}
            >
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ) : null}
    </Box>
  );
}
